import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { APP_STATUS, skillLabel } from "@/lib/constants";
import { imgSrc } from "@/lib/img";
import { SafetyConfirmDialog, hasSafetyAck } from "@/components/SafetyTipsDialog";
import { TrustChip } from "@/components/TrustChip";
import { Users, Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { maybeDonationNudge } from "@/lib/donate";
import FeedbackModal from "@/components/FeedbackModal";

// Org-side applicants management (accept/decline, bulk message, post-event
// feedback). Extracted from OpportunityDetail in the Phase 15 component split
// — behaviour unchanged.
const REJECT_REASONS = ["Role now filled", "Not enough availability", "Skills mismatch", "Location too far", "Other"];

export function ApplicantsPanel({ oppId, communityLed, helpRequest, oppTitle, eventPast, requirements }) {
  const nav = useNavigate();
  const [apps, setApps] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [feedbackFor, setFeedbackFor] = useState(null); // {opportunity_id, to_user_id, to_user_name, opportunity_title}
  const [givenSet, setGivenSet] = useState(new Set()); // volunteer_user_ids the org has already reviewed
  const [safetyFor, setSafetyFor] = useState(null); // applicant pending the safety acknowledgement

  const load = () => api.applicants(oppId).then(setApps).catch(() => {});
  useEffect(() => { load(); }, [oppId]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  useEffect(() => {
    if (!eventPast) return;
    api.feedbackMine().then(d => {
      const already = new Set(
        (d.given || [])
          .filter(f => f.opportunity_id === oppId && f.direction === "org_to_vol")
          .map(f => f.to_user_id)
      );
      setGivenSet(already);
    }).catch(() => {});
  }, [oppId, eventPast]);

  const accept = async (a) => {
    try {
      await api.decideApplication(a.id, "accept");
      if (helpRequest) {
        toast.success(`${a.volunteer_name} is your helper — other offers were politely declined`);
        load();
        maybeDonationNudge("Glad we could help! A small donation keeps CivicHearth running for everyone.", nav);
      } else {
        setApps(list => list.map(x => x.id === a.id ? { ...x, status: "accepted" } : x));
        toast.success(`${a.volunteer_name} accepted`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept"); }
  };

  // Accepting a help offer means meeting a neighbour — gate the first accept
  // on this device behind the safety prompt (org roles skip it).
  const requestAccept = (a) => {
    if (helpRequest && !hasSafetyAck()) return setSafetyFor(a);
    accept(a);
  };

  const reject = async () => {
    if (!reason) return toast.error("Pick a reason");
    try {
      await api.decideApplication(rejecting.id, "reject", reason, note);
      setApps(list => list.map(x => x.id === rejecting.id ? { ...x, status: "rejected" } : x));
      toast(`${rejecting.volunteer_name}'s application declined — they've been notified`);
      setRejecting(null); setReason(""); setNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not decline"); }
  };

  const sendBulk = async () => {
    const content = bulkText.trim();
    if (!content) return;
    try {
      const res = await api.bulkMessage(oppId, content);
      setBulkOpen(false); setBulkText("");
      toast.success(`Message sent to ${res.sent} accepted volunteer${res.sent !== 1 ? "s" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
  };

  const accepted = apps.filter(a => a.status === "accepted").length;
  const pending = apps.filter(a => a.status === "applied").length;
  // B2/B3: headcount — org rows count group_size (TOTAL people, no +1);
  // volunteer rows count 1 + party_size (self + guests).
  const liveCount = accepted + pending;
  const headcount = apps
    .filter(a => ["applied", "accepted"].includes(a.status))
    .reduce((s, a) => s + (a.org_application ? (a.group_size || 1) : 1 + (a.party_size || 0)), 0);

  return (
    <div data-testid="applicants-panel" className="mt-10 bg-white border border-neutral-200 rounded-2xl p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
          <Users className="w-4 h-4 text-terracotta" /> {helpRequest ? "Offers to help" : communityLed ? "Who's coming" : "Applicants"} ({apps.length})
        </div>
        <div className="text-xs text-neutral-500 flex items-center gap-3">
          <span data-testid="applicants-accepted-count"><b className="text-forest">{accepted}</b> accepted</span>
          <span data-testid="applicants-pending-count"><b className="text-amber-700">{pending}</b> pending</span>
          {headcount > liveCount && (
            <span data-testid="applicants-headcount"><b className="text-forest">{headcount}</b> people expected</span>
          )}
        </div>
        {accepted > 0 && (
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button data-testid="bulk-message-btn" variant="outline" size="sm" className="rounded-full h-9 text-xs">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Message all accepted ({accepted})
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="bulk-message-dialog">
              <DialogHeader>
                <DialogTitle>Message all accepted volunteers</DialogTitle>
                <DialogDescription>This sends an individual message to each of the {accepted} accepted {accepted === 1 ? "volunteer" : "volunteers"}.</DialogDescription>
              </DialogHeader>
              <Textarea data-testid="bulk-message-input" value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={4} placeholder="e.g. Reminder: meet at the park gates at 10am — bring gloves!" />
              <div className="flex justify-end">
                <Button data-testid="bulk-message-send" onClick={sendBulk} className="rounded-full bg-forest hover:bg-forest-hover">Send to {accepted}</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {/* B4: legal clarity — attestations are self-declared; verification is
            entirely the host's responsibility. */}
        {requirements && (
          <div data-testid="verify-yourself-note" className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
            Applicants self-attest to your requirements — CivicHearth doesn't verify anything.
            Please <b>verify documents yourself</b> (ID, DBS certificates) before the event.
          </div>
        )}
        {apps.length === 0 && <div className="text-sm text-neutral-500">{helpRequest ? "No offers yet — nearby volunteers have been alerted." : `No one has ${communityLed ? "joined" : "applied"} yet.`}</div>}
        <SafetyConfirmDialog
          open={!!safetyFor}
          onOpenChange={(v) => !v && setSafetyFor(null)}
          onConfirm={() => { const a = safetyFor; setSafetyFor(null); if (a) accept(a); }}
          confirmLabel="I understand — accept this offer"
        />
        {apps.map(a => {
          const st = APP_STATUS[a.status] || { label: a.status, cls: "bg-neutral-200 text-neutral-600" };
          return (
            <div key={a.id} data-testid={`applicant-row-${a.id}`} className="border border-neutral-100 rounded-xl p-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Avatar className="w-9 h-9 shrink-0">
                  <AvatarImage src={imgSrc(a.volunteer_picture)} alt={a.volunteer_name} />
                  <AvatarFallback className="bg-forest text-white text-xs">{a.volunteer_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <Link to={a.org_application ? `/o/${a.volunteer_user_id}` : `/u/${a.volunteer_user_id}`} className="font-medium text-forest text-sm hover:underline">{a.volunteer_name}</Link>
                  {/* 18-B1: trust chip (shield) — safety signal, distinct from medals */}
                  {a.volunteer_trust && <TrustChip trust={a.volunteer_trust} className="ml-1.5" />}
                  {/* B3: org-as-group applications carry the org identity — badge +
                      TOTAL headcount ("N people", group_size incl. the lead, no +1).
                      Volunteers keep the "+N others" framing. */}
                  {a.org_application && (
                    <Badge data-testid={`applicant-org-${a.id}`} className="rounded-full border-none text-[10px] bg-terracotta/10 text-terracotta ml-1.5 align-middle">
                      Organisation
                    </Badge>
                  )}
                  {a.org_application && ["applied", "accepted"].includes(a.status) ? (
                    <Badge data-testid={`applicant-party-${a.id}`} className="rounded-full border-none text-[10px] bg-forest/10 text-forest ml-1.5 align-middle">
                      {(a.group_size || 1) === 1 ? "1 person" : `${a.group_size} people`}
                    </Badge>
                  ) : !a.org_application && (a.party_size || 0) > 0 && ["applied", "accepted"].includes(a.status) && (
                    <Badge data-testid={`applicant-party-${a.id}`} className="rounded-full border-none text-[10px] bg-forest/10 text-forest ml-1.5 align-middle">
                      +{a.party_size} other{a.party_size > 1 ? "s" : ""}
                    </Badge>
                  )}
                  <div className="text-xs text-neutral-500 truncate">{a.volunteer_location || "—"}{a.team_name ? ` · Team ${a.team_name}` : ""}</div>
                </div>
                <Badge className={`rounded-full border-none text-xs shrink-0 ${st.cls}`}>{st.label}</Badge>
                <div className="flex gap-1.5 shrink-0">
                <button data-testid={`applicant-message-${a.id}`} onClick={() => nav(`/messages?to=${a.volunteer_user_id}`)}
                  className="text-xs px-2.5 py-1.5 rounded-full border border-neutral-200 text-neutral-600 hover:border-forest hover:text-forest transition-colors inline-flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> Message
                </button>
                {a.status === "applied" && (
                  <>
                    <button data-testid={`applicant-accept-${a.id}`} onClick={() => requestAccept(a)}
                      className="text-xs px-2.5 py-1.5 rounded-full bg-forest text-white hover:bg-forest-hover transition-colors">
                      Accept
                    </button>
                    <button data-testid={`applicant-reject-${a.id}`} onClick={() => setRejecting(a)}
                      className="text-xs px-2.5 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
                      Decline
                    </button>
                  </>
                )}
                {a.status === "accepted" && eventPast && (
                  givenSet.has(a.volunteer_user_id) ? (
                    <span data-testid={`feedback-given-${a.volunteer_user_id}`}
                      className="text-xs px-2.5 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
                      ⭐ Feedback given
                    </span>
                  ) : (
                    <button
                      data-testid={`feedback-open-${a.volunteer_user_id}`}
                      onClick={() => setFeedbackFor({
                        opportunity_id: oppId,
                        to_user_id: a.volunteer_user_id,
                        to_user_name: a.volunteer_name,
                        opportunity_title: oppTitle,
                      })}
                      className="text-xs px-2.5 py-1.5 rounded-full bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                    >
                      Leave feedback
                    </button>
                  )
                )}
                </div>
              </div>
              {a.attestations && (
                <div data-testid={`applicant-attest-${a.id}`} className="mt-2 flex flex-wrap gap-1.5">
                  {a.attestations.min_age && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-forest/10 text-forest">✓ self-attested {a.attestations.min_age}+</span>
                  )}
                  {a.attestations.dbs && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">✓ self-attested Enhanced DBS</span>
                  )}
                  {(a.attestations.skills || []).map((s) => (
                    <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-forest/10 text-forest capitalize">✓ {skillLabel(s)}</span>
                  ))}
                </div>
              )}
              {a.note ? (
                <div data-testid={`applicant-note-${a.id}`} className="mt-2 text-xs text-neutral-700 bg-neutral-50 rounded-lg px-3 py-2 whitespace-pre-line border-l-2 border-terracotta/40">
                  "{a.note}"
                </div>
              ) : (
                <div data-testid={`applicant-note-${a.id}`} className="mt-2 text-xs text-neutral-400 italic">
                  (no note provided)
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent data-testid="reject-application-dialog">
          <DialogHeader>
            <DialogTitle>Decline {rejecting?.volunteer_name}'s application</DialogTitle>
            <DialogDescription>They'll be notified with your reason and encouraged to keep volunteering.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="reject-reason-select" className="h-11"><SelectValue placeholder="Reason" /></SelectTrigger>
              <SelectContent>
                {REJECT_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea data-testid="reject-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add a personal note (optional)" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setRejecting(null)}>Cancel</Button>
              <Button data-testid="reject-confirm-btn" onClick={reject} className="rounded-full bg-red-600 hover:bg-red-700">Decline application</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <FeedbackModal
        open={!!feedbackFor}
        item={feedbackFor}
        onClose={() => setFeedbackFor(null)}
        onSubmitted={() => {
          if (feedbackFor?.to_user_id) {
            setGivenSet(prev => new Set([...prev, feedbackFor.to_user_id]));
          }
        }}
      />
    </div>
  );
}
